/* eslint-disable no-param-reassign */
/* eslint-disable guard-for-in */
/**
 * @author Fernando Ruano
 * @file utils.js
 * @description Multiple utils functions
 */

// Dependencies
import {
  SaveOutlined,
  FilePdfOutlined,
  PlaySquareOutlined,
  FileImageOutlined
} from '@ant-design/icons';
import { Input } from 'antd';
import moment from 'moment';
import localization from 'moment/locale/es';

// Components
import ButtonFilled from '@nephro/components/ButtonFilled';
import ButtonOutlined from '@nephro/components/ButtonOutlined';
import { debounce } from 'lodash';

/**
 * Sorts array of objects by string
 * @param { String } sortProperty - sort property
 * @param { 'descend' | 'ascend' } order - Order type
 * @return { Function }
 */
export const sortString = (sortProperty, order) => {
  return (a, b) => {
    if (order === 'descend') {
      return `${b[sortProperty]}`.localeCompare(a[sortProperty]);
    }
    return `${a[sortProperty]}`.localeCompare(b[sortProperty]);
  };
};

/**
 * Sorts array of objects by string
 * @param { String } sortProperty - sort property
 * @param { 'descend' | 'ascend' } order - Order type
 * @return { Function }
 */
export const sortStringPlus = (sortProperty) => {
  return (a) => {
    return `${a[sortProperty]}`.localeCompare(a[sortProperty]);
  };
};

/**
 * Sorts an object array of numbers
 * @param { String } property - property to sort
 * @param { String } mode - sort asc or desc
 * @return { Function }
 */
export const sortNumber = (property, mode = 'asc') => {
  if (mode === 'asc') {
    return (a, b) => a[property] - b[property];
  }
  return (a, b) => b[property] - a[property];
};

/**
 * Sort an object array by date
 * @param { Array } list - object array to sort
 * @param { String } key - sort property (object key)
 * @param { 'des' | 'asc' } mode - Order type
 * @return { Function }
 */
export const sortByDate = (list, key, mode = 'desc') =>
  list.sort((a, b) => {
    const result =
      mode === 'desc'
        ? new Date(b[key]) - new Date(a[key])
        : new Date(a[key]) - new Date(b[key]);
    return result;
  });

/**
 * Get last segment of url
 * @param { String } thePath - pathname
 * @return { Function }
 */
export const getLastItemUrl = (thePath) =>
  thePath.substring(thePath.lastIndexOf('/') + 1);

/**
 * Replace spaces by underscore
 * @param { String } string - current string
 * @return { Function }
 */
export const spaceByUnderscore = (string) => string.replace(' ', '_');

/**
 * Get item by id from array values
 * @param { String } array - Array to check
 * @param { String } idString -Id key string
 * @param { String } itemId - Id to find into array
 * @return { Function }
 */
export const onGetItemById = (array, idString, itemId) => {
  const itemFound = array.filter((itm) => itm[idString] === itemId);
  return itemFound[0];
};

/**
 * Get bottom options array to edit or create form info
 * @param { Function } onGoBack - onCancel Function
 * @param { Function } translate - translate Function
 * @param { String } mode - button text (create | edit)
 * @param { Array } buttonStyles - button styles array
 * @return { Array }
 */
export const bottomOptionsCreateEdit = (
  onGoBack,
  translate,
  mode = 'create',
  buttonStyles = []
) => [
  <ButtonOutlined
    key="1"
    type="cyan"
    textButton={translate('commons.cancel')}
    classStyles={[buttonStyles, 'mr-5'].join(' ')}
    onActionClick={onGoBack}
  />,
  <ButtonFilled
    key="2"
    type="cyan"
    htmlType="submit"
    textButton={
      mode === 'create' ? translate('commons.add') : translate('commons.save')
    }
    classStyles={[buttonStyles].join(' ')}
    iconComponent={<SaveOutlined style={{ marginTop: -2 }} />}
  />
];

/**
 * Get bottom options array to view form info
 * @param { Function } onGoBack - onCancel Function
 * @param { Function } translate - translate Function
 * @param { Array } buttonStyles - button styles array
 * @return { Array }
 */
export const bottomOptionsRead = (onGoBack, translate, buttonStyles = []) => [
  <ButtonFilled
    key="1"
    type="cyan"
    textButton={translate('commons.goback')}
    classStyles={[buttonStyles]}
    onActionClick={onGoBack}
  />
];

/**
 * Form item inputs to views (system and medical parameters)
 * @param { Function } translate - translate Function
 * @param { Bool } isReadOnly - Determinate if input is editable
 * @return { Array }
 */
export const formItemParameters = (translate, isReadOnly = false) => [
  {
    item: <Input readOnly={isReadOnly} />,
    name: ['param_key'],
    label: translate('paramMg.key'),
    rules: [
      {
        required: true
      }
    ],
    normalize: spaceByUnderscore,
    breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
  },
  {
    item: <Input readOnly={isReadOnly} />,
    name: ['param_value'],
    label: translate('paramMg.value'),
    rules: [
      {
        required: true
      }
    ],
    breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
  },
  {
    item: <Input readOnly={isReadOnly} />,
    name: ['param_description'],
    label: translate('commons.description'),
    breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
  }
];

/**
 * Form item inputs to views (profile and role views)
 * @param { Function } translate - translate Function
 * @param { Bool } isReadOnly - Determinate if input is editable
 * @return { Array }
 */
export const formItemNameDesc = (translate, isReadOnly = false) => [
  {
    item: <Input readOnly={isReadOnly} />,
    name: ['name'],
    label: translate('commons.name'),
    rules: [
      {
        required: true
      }
    ],
    breakpoints: { xs: 24, sm: 12, md: 12, lg: 12 }
  },
  {
    item: <Input readOnly={isReadOnly} />,
    name: ['description'],
    label: translate('commons.description'),
    breakpoints: { xs: 24, sm: 12, md: 12, lg: 12 }
  }
];

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

/**
 * @param {number} monthIndex - Index of month
 * @returns Month name
 */
export const getMonthName = (monthIndex) => {
  return `commons.${MONTH_NAMES[monthIndex]}`;
};

/**
 * Validate adherence status (Bueno or puede mejorar)
 * @param { Number } stage - stage value
 * @param { Number } indicatorDate - last date
 * @return { Array }
 */
export const onGetAdherence = (stage, indicatorDate) => {
  let adherence = 'es Buena';

  const today = moment();
  const lastDate = moment(indicatorDate);
  const lastMonth = today.subtract(1, 'month');
  const lastThreeMonth = moment().subtract(4, 'months');
  const lastSixMonth = moment().subtract(7, 'months');
  const lastMonthDiff = lastMonth.diff(lastDate, 'months');
  const lastTrimesterDiff = lastThreeMonth.diff(lastDate, 'months');
  const lastSemesterDiff = lastSixMonth.diff(lastDate, 'months');

  switch (stage) {
    case '5':
      adherence = lastMonthDiff === 0 ? adherence : 'puede Mejorar';
      return adherence;
    case '4':
      adherence = lastTrimesterDiff <= 0 ? adherence : 'puede Mejorar';
      return adherence;
    default:
      adherence = lastSemesterDiff <= 0 ? adherence : 'puede Mejorar';
      return adherence;
  }
};

/** Get month short name
 * @param {number} monthIndex - Index of month
 * @returns Month short name
 */
export const getMonthShortName = (monthIndex) => {
  return `commons.shortMonths.${MONTH_NAMES[monthIndex]}`;
};

/** Get date formated
 * @param {string} date - Date string
 * @param {string} format - Date format string
 * @returns Date formated
 */
export const getFormatedDate = (date, format) =>
  moment.utc(date).locale('es', localization).format(format);

/**
 * Validate blood pressure state (normal, high or low)
 * @param { Number } systolic - systolic value
 * @param { Number } diastolic - diastolic value
 * @return { Array }
 */
export const bloodPressureValidation = (systolic, diastolic) => {
  let state = 'normal';
  if (systolic >= 160 || diastolic >= 100) state = 'tooHigh';
  else if (systolic < 90 || diastolic < 60) state = 'tooLow';
  return state;
};

/**
 * Validate glucometry state (normal, high or low)
 * @param { Number } value - Glucometry value
 * @return { Array }
 */
export const glucometryValidation = (value) => {
  let state = 'normal';
  if (value > 200) state = 'tooHigh';
  else if (value < 70) state = 'tooLow';
  return state;
};

/**
 * Validate weight state (normal, preHigh, high or low)
 * @param { Number } value - Weight value
 * @return { Array }
 */
export const onGetWeightType = (val) => {
  let weightType = 'normal';
  if (val < 18.5) weightType = 'low';
  else if (val >= 25 && val < 30) weightType = 'preHigh';
  else if (val >= 30) weightType = 'high';
  return weightType;
};

/**
 * Validate password conditions
 * @param { String } rule
 * @param { String } value - String to validate
 * @param { function } callback - Callback function
 * @return { Array }
 */
export const validatePassword = (rule, value, callback) => {
  let validation = 0;

  // Length Validation
  validation = value?.length > 5 ? validation : 1;

  // Number Validation
  validation = /\d/.test(value) ? validation : 2;

  // UpperCase Validation
  validation = /[A-Z]/.test(value) ? validation : 3;

  // LowerCase Validation
  validation = /[a-z]/.test(value) ? validation : 4;

  if (value) {
    // Return validation if applies
    switch (validation) {
      case 1:
        return callback('La contraseña debe contener mínimo 8 caracteres.');
      case 2:
        return callback('La contraseña debe contener al menos un número.');
      case 3:
        return callback(
          'La contraseña debe contener al menos una letra mayúscula.'
        );
      case 4:
        return callback(
          'La contraseña debe contener al menos una letra minúscula.'
        );
      default:
        return Promise.resolve();
    }
  } else {
    return Promise.resolve();
  }
};

const style = {
  fontSize: 30,
  color: '',
  textAlign: 'left'
};

/**
 * Educational element type constants
 * @return { object }
 */
export const educationElementType = [
  {
    id: 1,
    name: 'pdf',
    desc: 'Archivo PDF',
    icon: <FilePdfOutlined style={{ ...style, color: 'var(--color-error)' }} />
  },
  {
    id: 2,
    name: 'image',
    desc: 'Imagen',
    icon: (
      <FileImageOutlined
        style={{ ...style, color: 'var(--color-brand-green)' }}
      />
    )
  },
  {
    id: 3,
    name: 'video',
    desc: 'Video',
    icon: (
      <PlaySquareOutlined
        style={{ ...style, color: 'var(--color-baxterBlue)' }}
      />
    )
  }
];

export const hasActiveVideoCall = (appointment) => {
  const currentTime = moment().utc(true);
  const dateTime = moment.utc(
    `${appointment.cap_fecha} ${appointment.cap_hora}`
  );
  const minuts = currentTime.diff(dateTime, 'minutes');
  return minuts <= 30 && minuts >= 0;
};

export const disabledFutureDates = (current) => {
  return current && current > moment().endOf('hours');
};

export const disabledFutureHours = () => {
  const currentHour = moment().hour();
  const disabledHours = [];
  for (let index = currentHour; index <= 24; index += 1) {
    disabledHours.push(index + 1);
  }
  return disabledHours;
};

export const disabledFutureMinuts = (selectedHour) => {
  const currentHour = moment().hour();
  const currentMinuts = moment().minute();
  const disabledMinuts = [];
  if (currentHour === selectedHour) {
    for (let index = currentMinuts; index <= 60; index += 1) {
      disabledMinuts.push(index + 1);
    }
  }
  return disabledMinuts;
};

export const transformPatientName = (name) => {
  const nameSplit = name.split(' ');
  const nameTransformed = nameSplit.map((item) => {
    if (item === 'del' || item === 'de' || item === 'la' || item === 'los') {
      return item;
    }
    return item.charAt(0).toUpperCase() + item.slice(1);
  });

  return nameTransformed.join(' ');
};

export const removeNullValuesFromObject = (obj) => {
  if (obj != null) {
    Object.keys(obj).forEach((key) => {
      if (obj[key] === null) {
        delete obj[key];
      }
    });
  }
  return obj;
};

export const joinArrayObject = (obj) => {
  const respObject = {};
  if (obj != null) {
    Object.keys(obj).forEach((key) => {
      if (obj[key] instanceof Array) {
        respObject[key] = obj[key].join(',');
      }
    });
  }
  return respObject;
};

export const formatDate = (date) => {
  return date ? moment.utc(date).format('DD-MM-YYYY') : '';
};

export const customDelay = (miliSeconds) => {
  return new Promise((resolve) => setTimeout(resolve, miliSeconds));
};

export const tableSearchClientMode = debounce(
  (
    keyword,
    searchableFields,
    initialData,
    setDataFilteredCallback,
    reloadDataCallback
  ) => {
    const cleanValue = keyword.trim().toLowerCase();
    if (cleanValue.length >= 3) {
      const foundItems = initialData.filter((row) => {
        return searchableFields.some((field) => {
          return row[field] && row[field].toLowerCase().includes(cleanValue);
        });
      });
      setDataFilteredCallback(foundItems);
    } else if (cleanValue.length === 0) {
      reloadDataCallback();
    }
  },
  400
);
